<template>
  <RsPartialNavigationContainer
    v-if="['sites', 'floorplan_navigator', 'unit_list', 'property_search'].some(p => $rsGlobals.$userHasAtLeastOneAccess(p))"
    :key="variant"
  >
    <RsPartialNavigationTree
      title="Websites"
      class="rs-text-white"
      icon="far fa-globe rs-text-tertiary-medium-purple"
      active-icon="fas fa-globe rs-text-primary"
      :variant="variant"
      icon-size="lg"
      :active="routeStartsWith('/websites') || routeStartsWith('/floorplan-navigator') || routeStartsWith('/unit-list-builder') || routeIncludes('/property-search')"
      :expanded="expanded"
    >
      <RsPartialNavigationTreeBranch v-if="$rsGlobals.$userHasAtLeastOneAccess('sites')">
        <RsPartialNavigationTree
          title="Website List"
          class="rs-text-white"
          to="/websites"
          :active="routeEndsWith('/websites')"
          hide-expand-icon
        >
          <RsPartialNavigationTree
            v-if="routeStartsWith('/websites')
              && routeParamExists('websiteId')
            "
            :key="$route.params.websiteId"
            title="Selected Website"
            class="rs-text-white"
            :title-fetcher="getWebsiteTitleFetcher"
            :expanded="routeParamExists('websiteId')"
            :active="routeParamExists('websiteId')"
          >
            <RsPartialNavigationTreeBranch
              v-if="$rsGlobals.$userHasAccess({
                keyType: 'site', key: 'menu_editor', accessType: 'read', idParam: 'websiteId',
              })"
              title="Menu"
              :active="routeParamExists('websiteId') && routeEndsWith('menu')"
              :to="{ name: 'Menu' }"
            />
            <RsPartialNavigationTreeBranch
              v-if="$rsGlobals.$userHasAccess({
                keyType: 'site', key: 'pages', accessType: 'read', idParam: 'websiteId',
              })"
              title="Pages"
              :active="routeParamExists('websiteId') && routeEndsWith('/pages')"
              :to="{ name: 'Pages' }"
            />
            <RsPartialNavigationTreeBranch
              v-if="showPostNav"
              title="Posts"
              :active="routeParamExists('websiteId') && routeIncludes('/posts')"
              :to="{ name: 'Posts' }"
            />
            <RsPartialNavigationTreeBranch
              v-if="$rsGlobals.$userHasAccess({
                keyType: 'site', key: 'cities', accessType: 'read', idParam: 'websiteId',
              })"
              title="City Pages"
              :active="routeParamExists('websiteId') && routeEndsWith('/citypages')"
              :to="{ name: 'CityPages' }"
            />
            <RsPartialNavigationTreeBranch
              v-if="$rsGlobals.$userHasAccess({
                keyType: 'site', key: 'site_management', accessType: 'read', idParam: 'websiteId',
              })"
              title="Global Content"
              :active="routeParamExists('websiteId') && routeEndsWith('/global-content')"
              :to="{ name: 'WebsiteCustomFields' }"
            />
            <RsPartialNavigationTreeBranch
              v-if="$rsGlobals.$userHasAccess({
                keyType: 'site', key: 'site_management', accessType: 'read', idParam: 'websiteId',
              })"
              title="Website Settings"
              :active="routeParamExists('websiteId') && routeEndsWith('/settings/global')"
              :to="{ name: 'WebsiteSettings' }"
            />
            <RsPartialNavigationTreeBranch
              v-if="['form_settings', 'inquiries'].some(key => $rsGlobals.$userHasAccess({
                keyType: 'site', key, accessType: 'read', idParam: 'websiteId',
              }))"
            >
              <RsPartialNavigationTree
                title="Forms"
                class="rs-text-white"
                :expanded="routeIncludes('/form-submissions') || routeIncludes('/forms/settings')"
                :active="routeIncludes('/form-submissions') || routeIncludes('/forms/settings')"
              >
                <RsPartialNavigationTreeBranch
                  v-if="$rsGlobals.$userHasAccess({
                    keyType: 'site', key: 'inquiries', accessType: 'read', idParam: 'websiteId',
                  })"
                  title="Submissions"
                  :active="routeParamExists('websiteId') && routeIncludes('/form-submissions')"
                  :to="{ name: 'FormSubmissions' }"
                />
                <RsPartialNavigationTreeBranch
                  v-if="$rsGlobals.$userHasAccess({
                    keyType: 'site', key: 'form_settings', accessType: 'read', idParam: 'websiteId',
                  })"
                  title="Settings"
                  :active="routeParamExists('websiteId') && routeIncludes('/forms/settings')"
                  :to="{ name: 'WebsiteFormSettingsAlt' }"
                />
              </RsPartialNavigationTree>
            </RsPartialNavigationTreeBranch>
            <RsPartialNavigationTreeBranch
              v-if="$rsGlobals.$userHasAccess('superuser')"
            >
              <RsPartialNavigationTree
                title="Superadmin Settings"
                class="rs-text-white"
                :expanded="routeIncludes('/superadmin-settings/')"
                :active="routeIncludes('/superadmin-settings/')"
              >
                <RsPartialNavigationTreeBranch
                  title="Global Settings"
                  :active="routeParamExists('websiteId') && routeEndsWith('/superadmin-settings/global')"
                  :to="{ name: 'GlobalSettings' }"
                />
                <RsPartialNavigationTreeBranch
                  title="Language Settings"
                  :active="routeParamExists('websiteId') && routeEndsWith('/superadmin-settings/language')"
                  :to="{ name: 'LanguageSettings' }"
                />
                <RsPartialNavigationTreeBranch
                  title="APIs & Keys"
                  :active="routeParamExists('websiteId') && routeEndsWith('/superadmin-settings/api-keys')"
                  :to="{ name: 'APIKeySettings' }"
                />
                <RsPartialNavigationTreeBranch
                  v-if="isNewFramework"
                  title="Tag Scripts"
                  :active="isNewFramework && routeParamExists('websiteId') && routeEndsWith('/superadmframeworkVersionin-settings/tag-scripts')"
                  :to="{ name: 'TagScripts' }"
                />
                <RsPartialNavigationTreeBranch
                  v-if="!isNewFramework"
                  title="Footer Scripts"
                  :active="routeParamExists('websiteId') && routeEndsWith('/superadmin-settings/footer-scripts')"
                  :to="{ name: 'FooterScripts' }"
                />
                <RsPartialNavigationTreeBranch
                  title="Panels"
                  :active="routeParamExists('websiteId') && routeIncludes('/superadmin-settings/panels')"
                  :to="{ name: 'WebsitePanelTemplates' }"
                />
              </RsPartialNavigationTree>
            </RsPartialNavigationTreeBranch>
          </RsPartialNavigationTree>
        </RsPartialNavigationTree>
      </RsPartialNavigationTreeBranch>

      <RsPartialNavigationTreeBranch
        v-if="$rsGlobals.$userHasAtLeastOneAccess('floorplan_navigator') && !$rsGlobals.$currentUser.isSuperUser"
      >
        <RsPartialNavigationTree
          title="Floorplan Navigator"
          class="rs-text-white"
          to="/floorplan-navigator"
          :active="routeEndsWith('/floorplan-navigator')"
          hide-expand-icon
        >
          <RsPartialNavigationTree
            v-if="routeStartsWith('/floorplan-navigator') && routeParamExists('fpnInstanceId')"
            :key="$route.params.websiteId"
            title="Selected FPN"
            class="rs-text-white"
            :title-fetcher="getFPNTitleFetcher"
            :expanded="routeParamExists('fpnInstanceId')"
            :active="routeParamExists('fpnInstanceId')"
          >
            <RsPartialNavigationTreeBranch
              v-if="$rsGlobals.$userHasAccess('floorplan_navigator', 'update')"
              title="Details"
              :active="routeParamExists('fpnInstanceId') && routeEndsWith('details')"
              :to="{ name: 'FloorplanNavigatorNonSuperUserDetails' }"
            />
          </RsPartialNavigationTree>
        </RsPartialNavigationTree>
      </RsPartialNavigationTreeBranch>

      <RsPartialNavigationTreeBranch
        v-if="$rsGlobals.$userHasAtLeastOneAccess('floorplan_navigator') && $rsGlobals.$currentUser.isSuperUser"
      >
        <RsPartialNavigationTree
          title="Floorplan Navigator"
          class="rs-text-white"
          :expanded="routeStartsWith('/floorplan-navigator')"
          :active="routeStartsWith('/floorplan-navigator')"
        >
          <RsPartialNavigationTreeBranch>
            <RsPartialNavigationTree
              title="Floorplan Navigators"
              class="rs-text-white"
              to="/floorplan-navigator"
              :active="routeEndsWith('/floorplan-navigator')"
              hide-expand-icon
            >
              <RsPartialNavigationTree
                v-if="routeStartsWith('/floorplan-navigator') && routeParamExists('fpnInstanceId')"
                :key="$route.params.websiteId"
                title="Selected FPN"
                class="rs-text-white"
                :title-fetcher="getFPNTitleFetcher"
                :expanded="routeParamExists('fpnInstanceId')"
                :active="routeParamExists('fpnInstanceId')"
              >
                <template
                  v-if="$route.params.fpnInstanceId !== 'new'"
                >
                  <RsPartialNavigationTreeBranch
                    v-if="$rsGlobals.$currentUser.isSuperUser"
                    title="SuperAdmin Details"
                    :active="routeParamExists('fpnInstanceId') && routeEndsWith('internal-details')"
                    :to="{ name: 'FloorplanNavigatorDetails' }"
                  />
                  <RsPartialNavigationTreeBranch
                    v-if="$rsGlobals.$currentUser.isSuperUser"
                    title="SuperAdmin Settings"
                    :active="routeParamExists('fpnInstanceId') && routeEndsWith('settings')"
                    :to="{ name: 'FloorplanNavigatorSettings' }"
                  />
                  <RsPartialNavigationTreeBranch
                    v-if="$rsGlobals.$currentUser.isSuperUser"
                    title="SuperAdmin Units"
                    :active="routeParamExists('fpnInstanceId') && routeEndsWith('units')"
                    :to="{ name: 'FloorplanNavigatorUnits' }"
                  />
                </template>
                <RsPartialNavigationTreeBranch
                  v-if="$rsGlobals.$currentUser.isSuperUser"
                  title="Points of Interest"
                  :active="routeParamExists('fpnInstanceId') && routeEndsWith('points-of-interest')"
                  :to="{ name: 'FloorplanNavigatorPointsOfInterest' }"
                />
              </RsPartialNavigationTree>
            </RsPartialNavigationTree>
          </RsPartialNavigationTreeBranch>

          <RsPartialNavigationTreeBranch v-if="$rsGlobals.$currentUser.isSuperUser">
            <RsPartialNavigationTree
              title="Templates"
              class="rs-text-white"
              :to="{ name: 'FPNTemplates' }"
              :active="routeEndsWith('/floorplan-navigator/templates')"
              hide-expand-icon
            >
              <RsPartialNavigationTree
                v-if="routeStartsWith('/floorplan-navigator/templates') && routeParamExists('templateId')"
                :key="$route.params.websiteId"
                title="Selected FPN Template"
                class="rs-text-white"
                :title-fetcher="getFPNTemplateTitleFetcher"
                :expanded="routeParamExists('templateId')"
                :active="routeParamExists('templateId')"
              >
                <RsPartialNavigationTreeBranch
                  title="Details"
                  :active="routeParamExists('templateId') && routeEndsWith('details')"
                  :to="{ name: 'FPNTemplateDetails' }"
                />
                <RsPartialNavigationTreeBranch
                  v-if="Number($route.params.templateId)"
                  title="Settings"
                  :active="routeParamExists('templateId') && routeEndsWith('/settings')"
                  :to="{ name: 'FPNSettings' }"
                />
                <RsPartialNavigationTreeBranch
                  v-if="Number($route.params.templateId)"
                  title="Listing Cards"
                  :active="routeParamExists('templateId') && routeEndsWith('listing-card-fields')"
                  :to="{ name: 'FPNTemplateListingCardFields' }"
                />
                <RsPartialNavigationTreeBranch
                  v-if="Number($route.params.templateId)"
                  title="Filters"
                  :active="routeParamExists('templateId') && routeEndsWith('filters')"
                  :to="{ name: 'FPNTemplateFilters' }"
                />
                <RsPartialNavigationTreeBranch
                  v-if="Number($route.params.templateId)"
                  title="Modal Tabs"
                  :active="routeParamExists('templateId') && routeEndsWith('modal-settings')"
                  :to="{ name: 'FPNTemplateModalSettings' }"
                />
                <RsPartialNavigationTreeBranch
                  v-if="Number($route.params.templateId)"
                  title="Modal Buttons"
                  :active="routeParamExists('templateId') && routeEndsWith('modal-buttons')"
                  :to="{ name: 'FPNTemplateModalButtons' }"
                />
                <RsPartialNavigationTreeBranch
                  v-if="Number($route.params.templateId)"
                  title="Modal Media Buttons"
                  :active="routeParamExists('templateId') && routeEndsWith('modal-media-buttons')"
                  :to="{ name: 'FPNTemplateModalMediaButtons' }"
                />
                <RsPartialNavigationTreeBranch
                  v-if="Number($route.params.templateId)"
                  title="Form Settings"
                  :active="routeParamExists('templateId') && routeEndsWith('form-settings')"
                  :to="{ name: 'FPNTemplateFormSettings' }"
                />
              </RsPartialNavigationTree>
            </RsPartialNavigationTree>
          </RsPartialNavigationTreeBranch>

          <RsPartialNavigationTreeBranch
            v-if="$rsGlobals.$currentUser.isSuperUser && corporateFormSettings"
            title="Form Settings"
            :to="corporateFormSettings"
          />
        </RsPartialNavigationTree>
      </RsPartialNavigationTreeBranch>

      <RsPartialNavigationTreeBranch
        v-if="$rsGlobals.$userHasAtLeastOneAccess('supersync_property_unit_search')
          && $rsGlobals.$userHasAtLeastOneAccess('unit_list')"
      >
        <RsPartialNavigationTree
          title="Unit List Builder"
          class="rs-text-white"
          :expanded="routeStartsWith('/unit-list-builder')"
          :active="routeStartsWith('/unit-list-builder')"
        >
          <RsPartialNavigationTreeBranch>
            <RsPartialNavigationTree
              title="Instances"
              class="rs-text-white"
              :to="{ name: 'UnitListIndex' }"
              :active="routeEndsWith('/unit-list-builder')"
              hide-expand-icon
            >
              <RsPartialNavigationTree
                v-if="routeStartsWith('/unit-list-builder') && routeParamExists('ulbId')"
                :key="$route.params.ulbId"
                title="Selected ULB Instance"
                class="rs-text-white"
                :title-fetcher="getULBTitleFetcher"
                :expanded="routeParamExists('ulbId')"
                :active="routeParamExists('ulbId')"
              >
                <RsPartialNavigationTreeBranch
                  title="Settings"
                  :active="routeEndsWith('settings')"
                  :to="{ name: 'TableSettings', params: { ulbId: $route.params.ulbId } }"
                />
                <div v-if="$route.params.ulbId !== 'new'">
                  <RsPartialNavigationTreeBranch
                    title="Fields Builder"
                    :active="routeEndsWith('fields-builder')"
                    :to="{ name: 'FieldsBuilder', params: { ulbId: $route.params.ulbId } }"
                  />
                  <RsPartialNavigationTreeBranch
                    title="Summary Builder"
                    :active="routeEndsWith('summary-builder')"
                    :to="{ name: 'SummaryBuilder', params: { ulbId: $route.params.ulbId } }"
                  />
                  <RsPartialNavigationTreeBranch
                    title="Filters Builder"
                    :active="routeEndsWith('filters-builder')"
                    :to="{ name: 'FiltersBuilder', params: { ulbId: $route.params.ulbId } }"
                  />
                  <RsPartialNavigationTreeBranch
                    title="Sorts Builder"
                    :active="routeEndsWith('sorts-builder')"
                    :to="{ name: 'SortsBuilder', params: { ulbId: $route.params.ulbId } }"
                  />
                  <RsPartialNavigationTreeBranch
                    title="Forms Builder"
                    :active="routeEndsWith('forms-builder')"
                    :to="{ name: 'FormsBuilder', params: { ulbId: $route.params.ulbId } }"
                  />
                </div>
              </RsPartialNavigationTree>
            </RsPartialNavigationTree>
          </RsPartialNavigationTreeBranch>
        </RsPartialNavigationTree>
      </RsPartialNavigationTreeBranch>

      <RsPartialNavigationTreeBranch
        v-if="$rsGlobals.$userHasAtLeastOneAccess('supersync_property_unit_search')
          && $rsGlobals.$userHasAtLeastOneAccess('property_search')"
      >
        <RsPartialNavigationTree
          title="Property Search"
          class="rs-text-white"
          :expanded="routeIncludes('/property-search')"
          :active="routeIncludes('/property-search')"
        >
          <RsPartialNavigationTreeBranch>
            <RsPartialNavigationTree
              title="Instances"
              class="rs-text-white"
              :to="{ name: 'PropertySearchIndex' }"
              :active="routeEndsWith('/property-search')"
              hide-expand-icon
            >
              <RsPartialNavigationTree
                v-if="routeParamExists('psiId')"
                :key="$route.params.psiId"
                title="Selected PSI"
                class="rs-text-white"
                :title-fetcher="getPSITitleFetcher"
                :expanded="routeParamExists('psiId')"
                :active="routeParamExists('psiId')"
              >
                <RsPartialNavigationTreeBranch
                  title="Settings"
                  :active="routeEndsWith('instances-builder')"
                  :to="{ name: 'PropertySearchInstancesBuilder', params: { psiId: $route.params.psiId } }"
                />
                <div v-if="$route.params.psiId !== 'new'">
                  <RsPartialNavigationTreeBranch
                    title="Fields Listing"
                    :active="routeEndsWith('fields-builder')"
                    :to="{ name: 'PropertyFieldsBuilder', params: { psiId: $route.params.psiId } }"
                  />
                  <RsPartialNavigationTreeBranch
                    title="Fields Map"
                    :active="routeEndsWith('fields-map-builder')"
                    :to="{ name: 'PropertyFieldsMapBuilder', params: { psiId: $route.params.psiId } }"
                  />
                  <RsPartialNavigationTreeBranch
                    title="Filters (Data)"
                    :active="routeEndsWith('data-filters-builder')"
                    :to="{ name: 'DataFiltersBuilder', params: { psiId: $route.params.psiId } }"
                  />
                  <RsPartialNavigationTreeBranch
                    title="Filters (UX)"
                    :active="routeEndsWith('ui-filters-builder')"
                    :to="{ name: 'UiFiltersBuilder', params: { psiId: $route.params.psiId } }"
                  />
                  <RsPartialNavigationTreeBranch
                    title="Sorting"
                    :active="routeEndsWith('instances-sort')"
                    :to="{ name: 'PropertyInstancesSort', params: { psiId: $route.params.psiId } }"
                  />
                </div>
              </RsPartialNavigationTree>
            </RsPartialNavigationTree>
          </RsPartialNavigationTreeBranch>

          <RsPartialNavigationTreeBranch>
            <RsPartialNavigationTree
              title="Map Settings"
              class="rs-text-white"
              :to="{ name: 'MapSettingsIndex' }"
              :active="routeEndsWith('/property-search/map-settings')"
              hide-expand-icon
            >
              <RsPartialNavigationTree
                v-if="routeParamExists('mapSettingId')"
                :key="$route.params.mapSettingId"
                :title="`Map ${$route.params.mapSettingId}`"
                class="rs-text-white"
                :expanded="routeParamExists('mapSettingId')"
                :active="routeParamExists('mapSettingId')"
              >
                <RsPartialNavigationTreeBranch
                  v-if="$route.params.mapSettingId !== 'new'"
                  title="Settings"
                  :active="routeIncludes('/property-search/map-settings/')"
                  :to="{ name: 'PropertyMapSettings', params: { mapSettingId: $route.params.mapSettingId } }"
                />
              </RsPartialNavigationTree>
            </RsPartialNavigationTree>
          </RsPartialNavigationTreeBranch>
        </RsPartialNavigationTree>
      </RsPartialNavigationTreeBranch>
    </RsPartialNavigationTree>
  </RsPartialNavigationContainer>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { IFloorplanNavigatorInstances, IFloorplanNavigatorPresets, IPropertySearchInstances, IUnitListBuilder } from '@llws/lift-entity-interfaces';
import RsPartialNavigationContainer from '@/components/RsPartialNavigationContainer.vue';
import RsPartialNavigationTree from '@/components/RsPartialNavigationTree.vue';
import RsPartialNavigationTreeBranch from '@/components/RsPartialNavigationTreeBranch.vue';
import { routeParamExists, routeStartsWith, routeEndsWith, routeIncludes } from '@/shared/helpers/navigation-active-helpers';
import websites from '@/shared/services/websites';
import { postModuleKeys } from '@/shared/helpers/module-keys';
import { decodeHtml } from '@/shared';
import { adminRequest } from '@/shared/request-configs/admin-request';

export default defineComponent({
  components: {
    RsPartialNavigationContainer,
    RsPartialNavigationTree,
    RsPartialNavigationTreeBranch,
  },

  props: {
    variant: {
      type: String,
      default: 'collapse',
    },
    expanded: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      tileExpanded: false,
      postModuleKeys,
    };
  },

  computed: {
    isNewFramework(): boolean {
      const site = this.$rsGlobals.$currentClient?.ownedSites?.find((s) => Number(s.id) === Number(this.$route.params.websiteId));
      return site?.frameworkVersion !== 'legacy';
    },
    showPostNav(): boolean {
      return this.postModuleKeys.some((mKey) => this.$rsGlobals.$userHasAccess({
        keyType: 'site',
        key: mKey,
        accessType: 'read',
        idParam: 'websiteId',
      }));
    },
    corporateFormSettings(): Record<string, any> | boolean {
      if (this.$rsGlobals.$currentClient.sites) {
        const site = this.$rsGlobals.$currentClient?.sites?.find((s) => s.sourceClientId === this.$rsGlobals.$currentClient?.id);

        if (site) {
          return {
            name: 'WebsiteFormSettingsAlt',
            params: { websiteId: site.id },
          };
        }
      }

      return false;
    },
  },

  methods: {
    routeParamExists,
    routeStartsWith,
    routeEndsWith,
    routeIncludes,

    async getWebsiteTitleFetcher(): Promise<string> {
      const site = await websites.getForSubheader(Number(this.$route.params.websiteId));
      return decodeHtml(site.data?.name);
    },

    async getFPNTitleFetcher(): Promise<string> {
      if (this.$route.params.fpnInstanceId === 'new') {
        return 'New FPN';
      }

      const querystring = '?select=b.buildingName&relations=buildings:b';
      const fpn = await adminRequest.get<IFloorplanNavigatorInstances>(
        `/web-components-api/floorplan_navigator_instances/${this.$route.params.fpnInstanceId}${querystring}`,
      );

      return decodeHtml(fpn.data?.buildings?.map((b) => b.buildingName).join(', ') ?? '');
    },

    async getPSITitleFetcher(): Promise<string> {
      if (!this.$route.params.psiId || this.$route.params.psiId === 'new') {
        return 'New Property Search';
      }

      const psi = await adminRequest.get<IPropertySearchInstances>(
        `/web-components-api/property_search_instances/${this.$route.params.psiId}`,
      );

      return decodeHtml(psi.data?.label ?? '');
    },

    async getULBTitleFetcher(): Promise<string> {
      if (!this.$route.params.ulbId || this.$route.params.ulbId === 'new') {
        return 'New Unit List Builder';
      }

      const ulb = await adminRequest.get<IUnitListBuilder>(
        `/web-components-api/unit_list_builder/${this.$route.params.ulbId}`,
      );

      return decodeHtml(ulb.data?.sortLabel ?? '');
    },

    async getFPNTemplateTitleFetcher(): Promise<string> {
      if (Number(this.$route.params.templateId)) {
        const querystring = '?select=id,name';
        const fpn = await adminRequest.get<IFloorplanNavigatorPresets>(
          `/web-components-api/floorplan_navigator_presets/${this.$route.params.templateId}${querystring}`,
        );
        return decodeHtml(fpn.data?.name ?? '');
      }

      return 'New Template';
    },
  },
});
</script>
